import { ThemeProps } from "./theme";
import { baseUrl } from ".";
import { getTenantTokenForAppId } from "./jwt2json";
import { logPwaEvent } from "./pwaEventTracking";
import { PWA_MANIFEST_REQUESTED } from "../constants/pwaEvents";
import {
  buildVersionedSwUrl,
  getSiteVersionForSw,
  isLegacySwRegistration,
  isVersionedSwController,
  registerVersionedServiceWorker,
  SW_MIGRATION_RELOAD_KEY,
} from "./swRegistrationCore";

const isDev = process.env.NODE_ENV !== "production";

// The non-standard event Chromium fires when a site becomes installable. Shared
// by the install modal, the store and pwaPromptState.
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// CloudFront origin serving uploaded whitelabel media (logos, favicons, PWA icons).
// Mirrors tva-api's UPLOADS_CDN_BASE so relative /uploads/... paths resolve to
// absolute URLs (same origin already used in toCDNImage.ts).
const UPLOADS_CDN_BASE = "https://d2qk9p4i741zfu.cloudfront.net";

const MANIFEST_REL = "manifest";
const APPLE_TOUCH_ICON_REL = "apple-touch-icon";
const APPLE_MOBILE_WEB_APP_CAPABLE = "apple-mobile-web-app-capable";
const APPLE_MOBILE_WEB_APP_TITLE = "apple-mobile-web-app-title";
const APPLE_MOBILE_WEB_APP_STATUS_BAR_STYLE = "apple-mobile-web-app-status-bar-style";
const APPLE_STATUS_BAR_DEFAULT = "default";
// Default home-screen label when a partner hasn't set their own short name.
// Mirrored in tva-api routes/whitelabelManifestHandler.js (manifest short_name).
const DEFAULT_PWA_SHORT_NAME = "Listings App";

const APPLE_TOUCH_ICON_SELECTOR = `link[rel="${APPLE_TOUCH_ICON_REL}"]`;
const APPLE_TITLE_SELECTOR = `meta[name="${APPLE_MOBILE_WEB_APP_TITLE}"]`;

// Every head element this module owns. Used to tear them all down again when the
// PWA is disabled, so we never leave stale install metadata in the document head.
// Deliberately excludes link[rel="icon"] and meta[name="theme-color"], which are
// managed by Helmet in App.tsx.
const OWNED_HEAD_SELECTORS = [
  `link[rel="${MANIFEST_REL}"]`,
  APPLE_TOUCH_ICON_SELECTOR,
  `meta[name="${APPLE_MOBILE_WEB_APP_CAPABLE}"]`,
  APPLE_TITLE_SELECTOR,
  `meta[name="${APPLE_MOBILE_WEB_APP_STATUS_BAR_STYLE}"]`,
];

// Prefix a stored media path with the CDN origin unless it is already absolute.
// Same rule as tva-api's toAbsoluteUrl in whitelabelManifestHandler.js.
function toAbsoluteCdnUrl(storedPath?: string): string | null {
  if (!storedPath) return null;
  if (storedPath.startsWith("http://") || storedPath.startsWith("https://")) return storedPath;
  return UPLOADS_CDN_BASE + storedPath;
}

// Resolve the app (home-screen) icon for the current theme, preferring the
// dedicated PWA icons and falling back to favIcon/logo — the same order the
// server manifest and iOS apple-touch tags use. Returns an absolute CDN URL, or
// null when the theme has no usable image. Used by the install modal so it shows
// the app icon rather than the wider website logo.
export function getPwaIconUrl(theme: ThemeProps): string | null {
  return (
    toAbsoluteCdnUrl(theme.pwaIcon192) ||
    toAbsoluteCdnUrl(theme.pwaIcon512) ||
    toAbsoluteCdnUrl(theme.favIcon) ||
    toAbsoluteCdnUrl(theme.logo)
  );
}

function upsertHeadLink(rel: string, href: string): void {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertMetaTag(name: string, content: string): void {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function removeHeadElement(selector: string): void {
  document.querySelector(selector)?.remove();
}

function removePwaHeadTags(): void {
  OWNED_HEAD_SELECTORS.forEach(removeHeadElement);
}

// iOS "Add to Home Screen" ignores manifest icons, so the home-screen icon and
// full-screen behaviour come from these head tags rather than the manifest.
function applyIosTags(theme: ThemeProps): void {
  // Mirror the server manifest's fallback order (pwaIcon192 → pwaIcon512 →
  // favIcon → logo) so a pwaEnabled site without dedicated icons still gets an
  // iOS home-screen icon instead of a page screenshot.
  const iconUrl = getPwaIconUrl(theme);
  if (iconUrl) {
    upsertHeadLink(APPLE_TOUCH_ICON_REL, iconUrl);
  } else {
    removeHeadElement(APPLE_TOUCH_ICON_SELECTOR);
  }

  upsertMetaTag(APPLE_MOBILE_WEB_APP_CAPABLE, "yes");
  upsertMetaTag(APPLE_MOBILE_WEB_APP_STATUS_BAR_STYLE, APPLE_STATUS_BAR_DEFAULT);

  const shortName = (theme.pwaShortName || "").trim() || DEFAULT_PWA_SHORT_NAME;
  if (shortName) {
    upsertMetaTag(APPLE_MOBILE_WEB_APP_TITLE, shortName);
  } else {
    removeHeadElement(APPLE_TITLE_SELECTOR);
  }
}

export function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !(window.navigator as any).standalone;
}

export function isIOSChrome(): boolean {
  return isIOS() && /CriOS/i.test(navigator.userAgent);
}

// Inject the PWA head tags for the current theme: the manifest <link> pointing at
// the tested server endpoint, plus the iOS-specific apple-touch-icon and meta tags.
// Idempotent — when PWA is disabled or no site token is available it tears the
// owned tags back down (and warns in dev) so no stale install metadata is left.
export function applyPwaHeadTags(theme: ThemeProps): void {
  if (!theme?.pwaEnabled) {
    if (isDev) console.warn("[pwa] pwaEnabled is false — PWA head tags not injected");
    removePwaHeadTags();
    return;
  }

  const token = getTenantTokenForAppId();
  if (!token) {
    if (isDev) console.warn("[pwa] no site token available — PWA head tags not injected");
    removePwaHeadTags();
    return;
  }

  // A <link rel="manifest"> cannot send an Authorization header, so the site token
  // travels as a query param. It is a build-time, already-client-public JWT — no
  // new secret exposure. The server resolves appId, validates the icon config and
  // returns absolute CDN URLs.
  upsertHeadLink(MANIFEST_REL, `${baseUrl}/whitelabel/manifest?token=${encodeURIComponent(token)}`);
  // Fired here (not server-side) so it carries the real partner domain and only
  // logs when the manifest link is actually injected (pwaEnabled + token present).
  logPwaEvent(PWA_MANIFEST_REQUESTED);

  applyIosTags(theme);
}

// isStandalone now lives in ./displayMode (a dependency-free module the SW
// bootstrap bundle can pull in without dragging the rest of pwaUtils). Re-exported
// here so existing `import { isStandalone } from "./pwaUtils"` sites keep working.
export { isStandalone } from "./displayMode";

export function isMobile(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

// Primary registration runs from public/swRegisterBootstrap.js (bundled from
// swRegistrationCore.ts) immediately after bootstrap.js so siteVersion is available
// before the React bundle loads (including on cached shells).
export {
  buildVersionedSwUrl,
  getSiteVersionForSw,
  isLegacySwRegistration,
  isVersionedSwController,
  SW_MIGRATION_RELOAD_KEY,
};

// Test-only wrapper around the core registration. Production registers via
// swRegisterBootstrap.ts (which also wires the Phase-3 SW diagnostics callbacks);
// this wrapper intentionally omits them so it stays a thin registration shim.
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  return registerVersionedServiceWorker({ isDev });
}
